/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { api } from "../api";
import LocationPickerMap from "../components/LocationPickerMap";
import { saveLpoOffline } from "../utils/offlineLpoStore";
import { toast } from "react-hot-toast";

function isTokenValid(token) {
  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function getValidToken() {
  try {
    const lpoToken = localStorage.getItem("lpo_token");
    const userToken = localStorage.getItem("fluxonix_token");

    const lpoValid = lpoToken && isTokenValid(lpoToken);
    const userValid = userToken && isTokenValid(userToken);

    if (lpoValid) return { token: lpoToken, userType: "lpo" };
    if (userValid) return { token: userToken, userType: "user" };
    return null;
  } catch {
    return null;
  }
}

function getIdentity(decoded, userType) {
  if (userType === "lpo" && decoded.lpo_id) {
    return { key: "lpo_id", value: decoded.lpo_id };
  }
  if (decoded.user_id) {
    return { key: "user_id", value: decoded.user_id };
  }
  return { key: "user_id", value: decoded.sub || decoded.id || null };
}

export default function LPOFormVoice() {
  const navigate = useNavigate();

  const [voiceBlob, setVoiceBlob] = useState(null);
  const [recording, setRecording] = useState(false);
  const [photoHasGPS, setPhotoHasGPS] = useState(true);
  const [fallbackZone, setFallbackZone] = useState("");
  const mediaRecorderRef = useRef(null);
  const [timer, setTimer] = useState(120);
  const timerRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [markerPosition, setMarkerPosition] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionTitle, setSessionTitle] = useState("");
  const [category, setCategory] = useState("Incident Report");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState(["#AUDIT", "#Q4_2024"]);

  const canSubmit = Boolean(voiceBlob && selectedPhotos.length > 0 && !recording);

  useEffect(() => {
    const result = getValidToken();
    if (!result) navigate("/otp-login");
  }, [navigate]);

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    let mimeType = "";

    if (window.MediaRecorder && MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")) {
      mimeType = "audio/ogg; codecs=opus";
    } else if (window.MediaRecorder && MediaRecorder.isTypeSupported("audio/webm; codecs=opus")) {
      mimeType = "audio/webm; codecs=opus";
    }

    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);

    mediaRecorderRef.current = recorder;

    const chunks = [];
    recorder.ondataavailable = (e) => chunks.push(e.data);

    recorder.onstop = () => {
      const blobType = recorder.mimeType || mimeType || "audio/ogg";
      const blob = new Blob(chunks, { type: blobType });
      setVoiceBlob(blob);
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerRef.current);
    };

    recorder.start();
    setRecording(true);
    setTimer(120);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          recorder.stop();
          setRecording(false);
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast("Processing submission...");

    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");

    const result = getValidToken();
    if (!result) {
      setError("Session expired. Please log in again.");
      setIsSubmitting(false);
      navigate("/login");
      return;
    }

    const { token, userType } = result;

    if (!voiceBlob) {
      setError("Please record a voice note (max 2 minutes).");
      setIsSubmitting(false);
      return;
    }

    if (!selectedPhotos || selectedPhotos.length === 0) {
      setError("Please attach at least one photo before submitting.");
      setIsSubmitting(false);
      return;
    }

    if (!markerPosition && !fallbackZone && !photoHasGPS) {
      setError("Please capture your location before submitting.");
      setIsSubmitting(false);
      return;
    }

    const isValidPhoto = (file) =>
      file && file.type.startsWith("image/") && file.size < 10 * 1024 * 1024;

    const validPhotos = selectedPhotos.filter(isValidPhoto);

    const isOnline = navigator.onLine;
    const metadata = {
      timestamp: new Date().toISOString(),
      voiceBlob,
      photoBlobs: validPhotos,
    };

    if (!isOnline) {
      await saveLpoOffline(metadata);
      toast.success("Saved offline. Sync will happen when you are online.");
      setIsSubmitting(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append("voice_file", voiceBlob, "voice.webm");

      let decoded = {};
      try {
        decoded = jwtDecode(token);
      } catch {
        throw new Error("Invalid token — cannot decode identity");
      }
      const { key, value } = getIdentity(decoded, userType);
      if (!value) throw new Error("No identity found in token");
      fd.append(key, value);

      const res = await api.post("/upload-voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const submissionId = res.data?.submission_id;

      if (!submissionId) {
        throw new Error("Server did not return a submission ID");
      }

      if (validPhotos.length > 0) {
        try {
          const formData = new FormData();
          validPhotos.forEach((file) => formData.append("files", file));

          const uploadRes = await fetch("/api/upload-photos", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });

          const { urls: uploadedUrls } = await uploadRes.json();
          await api.post(
            "/lpo/update-media-urls",
            {
              submission_id: submissionId,
              photo_urls: uploadedUrls,
              fallback_zone: !photoHasGPS ? fallbackZone : null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (uploadErr) {
          console.error("Photo upload failed:", uploadErr);
        }
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const removePhoto = (index) => {
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-6 max-w-md text-center">
          <span className="material-symbols-outlined text-emerald-500 text-5xl mb-4">check_circle</span>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Submission Received</h3>
          <p className="text-slate-600 dark:text-slate-400">
            Thank you! Your voice submission has been received and is pending approval.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-8">
      {/* Capture Console */}
      <div className="col-span-12 lg:col-span-8 space-y-8">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-8">
            <div className="flex flex-col items-center justify-center py-12">
              {/* Recording Animation */}
              <div className="relative flex items-center justify-center mb-10">
                <div className={`absolute w-64 h-64 bg-primary/5 rounded-full ${recording ? 'animate-pulse' : ''}`}></div>
                <div className="absolute w-48 h-48 bg-primary/10 rounded-full"></div>
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                  className={`relative size-32 rounded-full flex items-center justify-center text-white shadow-2xl transition-transform active:scale-95 group ${
                    recording
                      ? 'bg-red-500 hover:bg-red-600 shadow-red-500/40'
                      : 'bg-primary hover:bg-primary/90 shadow-primary/40'
                  }`}
                >
                  <span
                    className="material-symbols-outlined text-5xl group-hover:scale-110 transition-transform"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    {recording ? 'stop' : 'mic'}
                  </span>
                </button>
              </div>

              {/* Timer */}
              <div className="flex items-center gap-1.5 mb-2">
                {recording && <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>}
                <span className="text-2xl font-mono font-bold tracking-tighter text-slate-800 dark:text-slate-100 uppercase">
                  {formatTime(timer)}
                </span>
              </div>
              <p className="text-slate-500 font-medium">
                {recording
                  ? `${timer}s remaining`
                  : voiceBlob
                  ? 'Voice note ready'
                  : 'Click to begin secure session'}
              </p>

              {/* Waveform Visualizer */}
              <div className="mt-8 flex items-end gap-1 h-12">
                {[2, 4, 8, 5, 3, 7, 10, 12, 8, 10, 7, 11, 6, 9, 4, 6, 3].map((height, i) => (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-all ${
                      recording
                        ? i >= 7 && i <= 11
                          ? 'bg-primary animate-pulse'
                          : 'bg-slate-200 dark:bg-slate-700'
                        : 'bg-slate-100 dark:bg-slate-800'
                    }`}
                    style={{ height: `${height * 4}px` }}
                  />
                ))}
              </div>
            </div>

            {/* Location & Session Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8 border-t border-slate-100 dark:border-slate-800">
              {/* Location Indicator */}
              <div
                className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setShowMap(!showMap)}
              >
                <div className="size-10 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center text-primary shadow-sm">
                  <span className="material-symbols-outlined">location_on</span>
                </div>
                <div className="flex flex-col flex-1">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                    Location Status
                  </span>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {markerPosition ? 'Location captured' : 'Tap to capture GPS location...'}
                  </p>
                  {markerPosition && (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {markerPosition.lat.toFixed(4)}° N, {markerPosition.lng.toFixed(4)}° W
                    </p>
                  )}
                </div>
                {markerPosition && (
                  <span className="material-symbols-outlined text-emerald-500">check_circle</span>
                )}
              </div>

              {/* Session Info */}
              <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                <div className="size-10 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center text-primary shadow-sm">
                  <span className="material-symbols-outlined">key</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                    Session Token
                  </span>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Session ID: FX-{Date.now().toString().slice(-6)}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Validated & Encrypted</p>
                </div>
              </div>
            </div>

            {/* Map (collapsible) */}
            {showMap && (
              <div className="mt-6 h-64 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                <LocationPickerMap onLocationChange={setMarkerPosition} />
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
            <span className="material-symbols-outlined text-red-500">error</span>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button onClick={() => setError("")} className="ml-auto">
              <span className="material-symbols-outlined text-red-400 hover:text-red-600">close</span>
            </button>
          </div>
        )}
      </div>

      {/* Details Panel */}
      <div className="col-span-12 lg:col-span-4 space-y-8">
        {/* Submission Details Form */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="font-bold text-slate-900 dark:text-white mb-6">Submission Details</h3>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Session Title</label>
              <input
                type="text"
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                placeholder="e.g. Site Audit - Block A"
                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Categorization</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
              >
                <option>Incident Report</option>
                <option>Routine Inspection</option>
                <option>Field Notes</option>
                <option>Evidence Gathering</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add optional details or context..."
                rows={4}
                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Security Tags</label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 flex items-center gap-1"
                  >
                    {tag}
                    <span
                      className="material-symbols-outlined text-[12px] cursor-pointer hover:text-red-500"
                      onClick={() => removeTag(tag)}
                    >
                      close
                    </span>
                  </span>
                ))}
                <button
                  type="button"
                  className="px-2 py-1 bg-primary/10 rounded text-[10px] font-bold text-primary flex items-center gap-1"
                >
                  + Add Tag
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Evidence Assets */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 dark:text-white">Evidence Assets</h3>
            <span className="text-[10px] font-bold text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded uppercase tracking-widest">
              Required
            </span>
          </div>

          <label className="flex flex-col items-center gap-4 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 bg-slate-50/50 dark:bg-slate-800/30 hover:border-primary/50 transition-colors cursor-pointer">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined text-2xl">add_photo_alternate</span>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-slate-900 dark:text-white">Drag photos here</p>
              <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-tight font-medium">
                PNG, JPG, HEIC up to 20MB
              </p>
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) {
                  setSelectedPhotos((prev) => [...prev, ...files]);
                }
                e.target.value = "";
              }}
            />
            <span className="w-full py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-bold text-center hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
              Browse Files
            </span>
          </label>

          {/* Photo Previews */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            {selectedPhotos.map((file, idx) => (
              <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                <img
                  src={URL.createObjectURL(file)}
                  alt={`preview-${idx}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 size-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                >
                  <span className="material-symbols-outlined text-xs">close</span>
                </button>
              </div>
            ))}
            {selectedPhotos.length < 4 && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 dark:text-slate-600 cursor-pointer hover:border-primary/50 transition-colors">
                <span className="material-symbols-outlined">add</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setSelectedPhotos((prev) => [...prev, file]);
                    e.target.value = "";
                  }}
                />
              </label>
            )}
          </div>

          {selectedPhotos.length === 0 && (
            <p className="text-xs text-red-500 mt-2">At least one photo is required</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="w-full py-4 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
